import {
  AppliedCoupon,
  ApplyCouponHandler,
  AssignedCoupon,
  RemoveCouponHandler,
  ShippingAddress,
} from '@app/types/cart/Coupon';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import Drawer from '@redbubble/design-system/src/Drawer';
import CloseBigIcon from '@redbubble/design-system/src/Icons/CloseBig';
import Text from '@redbubble/design-system/src/Text';
import { useViewportWidth } from '@redbubble/next/hooks';
import { useIntl } from '@redbubble/next/intl';
import { DeviceType } from '@redbubble/next/types';
import React, { FC, useMemo, useState } from 'react';
import { Coupon } from '../Coupon/Coupon';
import styles from '../Coupon.module.scss';
import { CouponInput } from '../CouponInput/CouponInput';
import { MemberOffers } from '../MemberOffers';
import { couponMessages } from '../messages';

interface ManageCouponsProps {
  locale: string;
  appliedCoupon?: AppliedCoupon;
  assignedCoupons?: AssignedCoupon[];
  setDrawerOrAccordionClosed: (boolean) => void;
  drawerOrAccordionClosed: boolean;
  isGetUserQueryServerError: boolean;
  onCouponAction: (coupon: AssignedCoupon, isApply: boolean) => void;
  isLoadingMemberOffers: boolean;
  doCouponCodeApply: ApplyCouponHandler;
  doCouponCodeRemove: RemoveCouponHandler;
  shippingAddress?: ShippingAddress;
}

export const ManageCoupons: FC<ManageCouponsProps> = ({
  locale,
  appliedCoupon,
  assignedCoupons,
  setDrawerOrAccordionClosed,
  drawerOrAccordionClosed,
  isGetUserQueryServerError,
  onCouponAction,
  isLoadingMemberOffers,
  doCouponCodeApply,
  doCouponCodeRemove,
  shippingAddress,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(!drawerOrAccordionClosed);

  const toggleModal = () => {
    setOpen(!open);
    setDrawerOrAccordionClosed(!drawerOrAccordionClosed);
  };
  const result = useViewportWidth();
  const { isOrAbove } = result;
  const userHasMultipleCoupons = useMemo(
    () => assignedCoupons && assignedCoupons.length > 1,
    [assignedCoupons]
  );

  return (
    <>
      <Box display="flex" marginBottom="s">
        <Text type="display5">
          {intl.formatMessage(couponMessages.couponGiftCard)}
        </Text>

        <button
          type="button"
          className={[
            styles.changeOrCloseButton,
            userHasMultipleCoupons ? styles.redColor : [],
          ].join(' ')}
          onClick={toggleModal}
        >
          {userHasMultipleCoupons &&
            open &&
            intl.formatMessage(couponMessages.closeCoupons)}

          {userHasMultipleCoupons &&
            !open &&
            intl.formatMessage(couponMessages.otherCoupons)}

          {!userHasMultipleCoupons &&
            !open &&
            intl.formatMessage(couponMessages.change)}

          {!userHasMultipleCoupons &&
            open &&
            intl.formatMessage(couponMessages.close)}
        </button>
      </Box>
      {open &&
        (isOrAbove(DeviceType.Tablet) ? (
          <>
            <CouponInput
              drawerOrAccordionClosed={drawerOrAccordionClosed}
              assignedCoupons={assignedCoupons}
              appliedCoupon={appliedCoupon}
              doCouponCodeApply={doCouponCodeApply}
              doCouponCodeRemove={doCouponCodeRemove}
              shippingAddress={shippingAddress}
            />
            <MemberOffers
              isGetUserQueryServerError={isGetUserQueryServerError}
              assignedCoupons={assignedCoupons}
              appliedCoupon={appliedCoupon}
              onCouponAction={onCouponAction}
              isLoading={isLoadingMemberOffers}
            />
          </>
        ) : (
          <Drawer
            accessibleTitle={intl.formatMessage(couponMessages.couponGiftCard)}
            open={open}
            from={constants.BOTTOM}
            size={constants.LARGE}
            fit
            onCloseRequested={toggleModal}
            getApplicationNode={() => document.getElementById('__next')}
          >
            <Box display="flex" justifyContent="space-between">
              <Box marginTop="xs" marginLeft="m">
                <Text type="display3">
                  {intl.formatMessage(couponMessages.couponGiftCard)}
                </Text>
              </Box>
              <Button
                strong
                circle
                inverse
                iconAfter={<CloseBigIcon size="medium" />}
                size={constants.LARGE}
                onClick={toggleModal}
              />
            </Box>
            <Box marginRight="m" marginLeft="m" marginBottom="m">
              <CouponInput
                drawerOrAccordionClosed={drawerOrAccordionClosed}
                assignedCoupons={assignedCoupons}
                appliedCoupon={appliedCoupon}
                doCouponCodeApply={doCouponCodeApply}
                doCouponCodeRemove={doCouponCodeRemove}
                shippingAddress={shippingAddress}
              />
              <MemberOffers
                isGetUserQueryServerError={isGetUserQueryServerError}
                onCouponAction={onCouponAction}
                isLoading={isLoadingMemberOffers}
                assignedCoupons={assignedCoupons}
                appliedCoupon={appliedCoupon}
              />
            </Box>
          </Drawer>
        ))}

      {!open && appliedCoupon && (
        <Coupon
          displayName={appliedCoupon.display_name}
          type={appliedCoupon.type}
          expiryDate={appliedCoupon.expiry_date}
          locale={locale}
        />
      )}
    </>
  );
};
